const generateId = () => globalThis.crypto.randomUUID()

const buildLanesMutationInput = (workflowId, expectedVersion, lanes, updateNote) => ({
  id: workflowId,
  expectedVersion,
  lanes,
  updateNote
})

export const buildCreateLaneMutation = ({ workflow, workflowId, name, position }) => {
  const lanes = workflow.lanes || []

  if (lanes.find((l) => l.name === name)) {
    return { error: `Lane "${name}" already exists` }
  }

  const newLane = { id: generateId(), name }

  let updatedLanes
  if (position === undefined || position === null) {
    updatedLanes = [...lanes, newLane]
  } else if (!Number.isInteger(position) || position < 0 || position > lanes.length) {
    return { error: `position must be an integer between 0 and ${lanes.length} (inclusive); received ${position}` }
  } else {
    updatedLanes = [...lanes]
    updatedLanes.splice(position, 0, newLane)
  }

  return {
    mutationInput: buildLanesMutationInput(workflowId, workflow.version, updatedLanes, {
      action: 'created lane',
      target: name
    }),
    name
  }
}

export const buildUpdateLaneMutation = ({ workflow, workflowId, lane, name, position }) => {
  const lanes = workflow.lanes || []
  const laneIndex = lanes.findIndex((l) => l.name === lane)

  if (laneIndex === -1) {
    return { error: `Lane "${lane}" not found` }
  }

  const matchedLane = lanes[laneIndex]

  if (name !== undefined && lanes.find((l) => l.name === name && l.id !== matchedLane.id)) {
    return { error: `Lane "${name}" already exists` }
  }

  if (position !== undefined && (!Number.isInteger(position) || position < 0 || position >= lanes.length)) {
    return { error: `position must be an integer between 0 and ${lanes.length - 1} (inclusive); received ${position}` }
  }

  const changes = []
  let updatedLanes = lanes.map((l) =>
    l.id === matchedLane.id && name !== undefined ? { ...l, name } : l
  )

  if (name !== undefined && name !== matchedLane.name) {
    changes.push(`renamed from "${matchedLane.name}" to "${name}"`)
  }

  // Vertical order is the lanes array order, so a reposition is a splice — events follow by laneId.
  if (position !== undefined && position !== laneIndex) {
    const laneToMove = updatedLanes.find((l) => l.id === matchedLane.id)
    updatedLanes = updatedLanes.filter((l) => l.id !== matchedLane.id)
    updatedLanes.splice(position, 0, laneToMove)
    changes.push(`moved to position ${position}`)
  }

  if (changes.length === 0) {
    return { error: 'No changes specified. Provide a new name and/or a different position.' }
  }

  return {
    mutationInput: buildLanesMutationInput(workflowId, workflow.version, updatedLanes, {
      action: 'updated lane',
      target: changes.join(', ')
    }),
    lane,
    name,
    changes
  }
}

export const buildDeleteLaneMutation = ({ workflow, workflowId, lane }) => {
  const lanes = workflow.lanes || []
  const laneIndex = lanes.findIndex((l) => l.name === lane)

  if (laneIndex === -1) {
    return { error: `Lane "${lane}" not found` }
  }

  const matchedLane = lanes[laneIndex]

  const eventsInLane = (workflow.domainEvents || []).filter((e) => e.laneId === matchedLane.id)
  if (eventsInLane.length > 0) {
    return {
      error: `Cannot delete lane "${lane}": it contains ${eventsInLane.length} event(s). Move or delete the events first.`
    }
  }

  const updatedLanes = lanes.filter((l) => l.id !== matchedLane.id)

  return {
    mutationInput: buildLanesMutationInput(workflowId, workflow.version, updatedLanes, {
      action: 'deleted lane',
      target: lane
    }),
    lane
  }
}
